import { useState, useEffect } from "react";
import { Save, AlertTriangle, CheckCircle, Fuel } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "../../utils/apiClient";
import { usesKm, usesHours } from "./readingType";

const BulkFuelEntryTab = ({ projectId, equipment, materials, onRefresh }) => {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [lastReadings, setLastReadings] = useState({});

  const dieselMaterial = materials.find(m => m.name?.toLowerCase().includes('diesel') || m.name?.toLowerCase().includes('fuel'));

  useEffect(() => {
    if (equipment.length > 0) {
      initRows();
      fetchLastReadings();
    }
  }, [equipment, date]);

  const initRows = () => {
    setRows(equipment.filter(eq => eq.status !== "Archived" && eq.fuel_type !== "Electric").map(eq => ({
      equipment_id: eq.id, name: eq.name, reg: eq.equipment_id,
      useKm: usesKm(eq), useHr: usesHours(eq),
      quantity: "", opening_km: 0, opening_hour: 0,
      closing_km: "", closing_hour: "",
      issued_by: "", received_by: "", remarks: "", touched: false
    })));
  };

  const fetchLastReadings = async () => {
    const readings = {};
    for (const eq of equipment) {
      try {
        const res = await apiClient.get(`/equipment/last-readings/${eq.id}`);
        if (res.data.has_previous) {
          readings[eq.id] = { 
            km: res.data.closing_km_reading || 0, 
            hour: res.data.closing_hour_reading || 0, 
            date: res.data.last_date 
          };
        }
      } catch (e) { /* ignore */ }
    }
    setLastReadings(readings);
    setRows(prev => prev.map(r => {
      const last = readings[r.equipment_id];
      return last ? { ...r, opening_km: last.km || 0, opening_hour: last.hour || 0 } : r;
    }));
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value, touched: true };
      return updated;
    });
  };

  const handleSaveAll = async () => {
    const toSave = rows.filter(r => r.touched && r.quantity && parseFloat(r.quantity) > 0);
    if (toSave.length === 0) {
      toast.error("No data to save. Enter quantity for at least one machine.");
      return;
    }
    if (!dieselMaterial) {
      toast.error("No diesel/fuel material found in inventory");
      return;
    }
    setSaving(true);
    let saved = 0, failed = 0;
    for (const r of toSave) {
      try {
        await apiClient.post("/equipment/fuel-issue", {
          project_id: projectId,
          equipment_id: r.equipment_id,
          material_id: dieselMaterial.id,
          date,
          quantity: parseFloat(r.quantity),
          current_km_reading: r.useKm ? (parseFloat(r.closing_km) || 0) : 0,
          current_hour_reading: r.useHr ? (parseFloat(r.closing_hour) || 0) : 0,
          issued_by: r.issued_by || "Site Supervisor",
          received_by: r.received_by || "Operator",
          remarks: r.remarks
        });
        saved++;
      } catch (e) {
        failed++;
        console.error(`Failed to save fuel for ${r.name}:`, e);
      }
    }
    setSaving(false);
    if (saved > 0) toast.success(`Issued fuel to ${saved} machine(s)`);
    if (failed > 0) toast.error(`Failed for ${failed} machine(s)`);
    if (saved > 0) {
      onRefresh?.();
      initRows();
      fetchLastReadings();
    }
  };

  const inputCls = "w-full px-2 py-1.5 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary font-mono";
  const hasWarning = (r) => (r.useKm && r.closing_km && parseFloat(r.closing_km) < r.opening_km) ||
                            (r.useHr && r.closing_hour && parseFloat(r.closing_hour) < r.opening_hour);
  const na = <span className="text-[10px] text-slate-400">N/A</span>;

  return (
    <div className="space-y-4" data-testid="bulk-fuel-entry-tab">
      <div className="stat-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1 uppercase tracking-widest">Date</label>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary"
                data-testid="bulk-fuel-date" />
            </div>
            <div className="text-sm text-slate-600 mt-5">
              <span className="font-medium">{rows.filter(r => r.touched && r.quantity).length}</span> of {rows.length} machines have fuel data
            </div>
            {dieselMaterial && (
              <div className="text-sm text-slate-500 mt-5">
                Fuel: <span className="font-medium text-warning">{dieselMaterial.name}</span> (Stock: {dieselMaterial.current_stock?.toFixed(0) || 0} L)
              </div>
            )}
          </div>
          <button onClick={handleSaveAll} disabled={saving || !dieselMaterial} className="btn-primary bg-warning hover:bg-warning/90 flex items-center gap-2" data-testid="bulk-fuel-save-btn">
            <Fuel className="w-4 h-4" /> {saving ? "Saving..." : "Issue Fuel to All"}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="bulk-fuel-table">
            <thead>
              <tr className="border-b-2 border-slate-300 text-[10px] uppercase tracking-widest text-slate-600">
                <th className="py-2 px-2 text-left w-[180px]">Equipment</th>
                <th className="py-2 px-2 text-center w-[70px]">Open KM</th>
                <th className="py-2 px-2 text-center w-[80px]">Close KM</th>
                <th className="py-2 px-2 text-center w-[70px]">Open Hr</th>
                <th className="py-2 px-2 text-center w-[80px]">Close Hr</th>
                <th className="py-2 px-2 text-center w-[80px]">Qty (L) *</th>
                <th className="py-2 px-2 text-left w-[120px]">Issued By</th>
                <th className="py-2 px-2 text-left w-[120px]">Received By</th>
                <th className="py-2 px-2 text-left">Remarks</th>
                <th className="py-2 px-2 w-[30px]"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={r.equipment_id} className={`border-b border-slate-200 ${r.touched && r.quantity ? "bg-amber-50/50" : ""}`} data-testid={`bulk-fuel-row-${r.equipment_id}`}>
                  <td className="py-2 px-2">
                    <div className="font-medium text-primary truncate" title={r.name}>{r.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{r.reg}</div>
                  </td>
                  <td className="py-1 px-1 text-center font-mono text-slate-500 text-xs">{r.useKm ? r.opening_km.toFixed(0) : na}</td>
                  <td className="py-1 px-1 text-center">{r.useKm ? <input type="number" step="0.1" value={r.closing_km} onChange={(e) => updateRow(idx, "closing_km", e.target.value)} className={inputCls} placeholder={r.opening_km.toString()} data-testid={`bulk-fuel-km-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1 text-center font-mono text-slate-500 text-xs">{r.useHr ? r.opening_hour.toFixed(1) : na}</td>
                  <td className="py-1 px-1 text-center">{r.useHr ? <input type="number" step="0.1" value={r.closing_hour} onChange={(e) => updateRow(idx, "closing_hour", e.target.value)} className={inputCls} placeholder={r.opening_hour.toString()} data-testid={`bulk-fuel-hr-${r.equipment_id}`} /> : na}</td>
                  <td className="py-1 px-1"><input type="number" step="0.1" value={r.quantity} onChange={(e) => updateRow(idx, "quantity", e.target.value)} className={`${inputCls} font-semibold bg-amber-50`} placeholder="0" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.issued_by} onChange={(e) => updateRow(idx, "issued_by", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Name" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.received_by} onChange={(e) => updateRow(idx, "received_by", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Name" /></td>
                  <td className="py-1 px-1"><input type="text" value={r.remarks} onChange={(e) => updateRow(idx, "remarks", e.target.value)} className={inputCls.replace("font-mono", "")} placeholder="Notes" /></td>
                  <td className="py-1 px-1 text-center">
                    {hasWarning(r) && <AlertTriangle className="w-4 h-4 text-amber-500 inline" title="Closing < Opening" />}
                    {r.touched && !hasWarning(r) && r.quantity && <CheckCircle className="w-4 h-4 text-green-500 inline" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="text-center py-8 text-slate-500">No equipment found.</div>
        )}
      </div>
    </div>
  );
};

export default BulkFuelEntryTab;
